<template>
  <div class="datasource-source-selector">
    <BittsTabs
      :tabs="tabs"
      class="mb-24"
      :current-value="activeTab"
      @change:tab="(val) => (activeTab = val)"
    />
    <div role="tabpanel" :aria-label="activeTabName" tabindex="0">
      <BittsTreeWithSearch
        v-if="!fieldsPending"
        v-model:search="searchQuery"
        :node-data="displayTree"
        name="source-selector-search"
        placeholder="Search"
        :search-debounce="500"
        class="datasource-source-selector__tree"
        @update="onUpdate"
      >
        <template #node-content="{ node, highlightTerm: term }">
          <div
            class="datasource-source-selector__node"
            :class="{
              'datasource-source-selector__node-disabled': node.isDisabled,
            }"
          >
            <component
              :is="tooltipFor(node.metaData).is"
              v-bind="tooltipFor(node.metaData).props"
              class="text-ellipsis whitespace-nowrap overflow-hidden"
            >
              <BittsTreeNodeLabel
                class="datasource-source-selector__label"
                :label="node.label"
                :highlight-term="term"
              />
              <span
                v-if="
                  node.metaData?.isRequired &&
                  node.metaData?.blockedPops?.length
                "
              >
                <BittsTooltip
                  :mount-to-body="true"
                  placement="top"
                  class="inline-flex pl-8 cursor-pointer"
                >
                  <FontAwesomeIcon
                    :icon="['fak', 'information-circle']"
                    :style="{ height: '14px', width: '14px' }"
                    class="text-neutral-accent"
                  />
                  <template #title>
                    In order to un-sync this field it must be removed from any
                    population definitions
                  </template>
                </BittsTooltip>
                <div class="c-bitts-tree_population-links">
                  <span
                    v-for="(pop, index) in node.metaData.blockedPops"
                    :key="pop.id"
                  >
                    <span v-if="!index" class="text-neutral-text">
                      Used in the&nbsp;
                    </span>
                    <RouterLink
                      :to="{
                        name: 'edit_population',
                        params: { population_id: pop.id },
                      }"
                      class="cursor-pointer inline-flex flex-nowrap items-center max-w-full"
                    >
                      <span class="shrink truncate text-neutral-text-weak">
                        {{ pop.name }} Population
                      </span>
                      <FontAwesomeIcon
                        :icon="['fak', 'open-link']"
                        :style="{
                          height: '8px',
                          width: '8px',
                          color: 'currentColor',
                        }"
                        class="text-white bg-info-accent ml-4 rounded p-2"
                      />
                    </RouterLink>
                  </span>
                </div>
              </span>
              <template #title>
                {{ node.metaData?.fieldTooltipText }}
              </template>
            </component>
            <span
              v-if="
                node.metaData?.isRequired && !node.metaData?.blockedPops?.length
              "
              class="text-xs text-neutral-text-weak/50"
            >
              Required for {{ node.metaData?.tableName }} Object
            </span>
            <BittsTooltip
              v-else-if="node.metaData?.hasIcon"
              :mount-to-body="true"
              overlay-class="c-bitts-tree_question-tooltip-overlay cursor-default"
              placement="top"
              @click.stop
            >
              <FontAwesomeIcon
                :icon="['far', 'file-magnifying-glass']"
                :style="{
                  height: '16px',
                  width: '16px',
                  color: 'currentColor',
                }"
                class="text-neutral-accent"
                @click.stop
              />
              <template #title>
                <div class="c-bitts-tree_question-tooltip">
                  {{ node.metaData?.tooltipText }}
                </div>
              </template>
            </BittsTooltip>
          </div>
        </template>
      </BittsTreeWithSearch>
      <div v-else class="flex flex-col items-center h-[150px] justify-center">
        <div class="flex items-center text-neutral-text-placeholder text-m">
          <FontAwesomeIcon
            :icon="['fak', 'cycle']"
            :style="{ height: '10px', width: '10px', color: 'currentColor' }"
            class="text-neutral-text-placeholder mr-4"
          />
          <span> Some fields are syncing. </span>
        </div>
        <div class="text-base text-neutral-text-placeholder flex flex-row">
          Check back later or
          <button
            type="button"
            class="text-info-text ml-3 cursor-pointer appearance-none border-0 bg-transparent p-0"
            @click="$emit('refreshFields')"
          >
            click here to refresh
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsTabs,
  BittsTooltip,
  BittsTreeNodeLabel,
  BittsTreeWithSearch,
  type TreeNodeType,
  applyCheckedChange,
  findNode,
} from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';

import {
  ALL_FIELDS,
  CANNOT_UNSYNC,
  CANNOT_UNSYNC_PLURAL,
  type CopyingField,
  LOOKUP_FIELD,
  type Message,
  SELECTED_FIELDS,
  SOURCE,
  SOURCE_FIELD,
  SYNCED,
  UNSYNCED,
  UNSYNCED_DISABLED,
  getFieldTooltipText,
  getKey,
} from '@/constants/data_sources';
import { useSourcesStore } from '@/stores';
import type { Source, SourceField } from '@/types/sources';

type NodeValue = {
  type: string;
  id: number;
  source_id?: number;
  required?: boolean;
  requiresUncheckedField?: boolean;
  requiredForLookup?: boolean;
  hasCheckedCopyingFields?: boolean;
  hasRequiredFields?: boolean;
  relationship_id?: string | null;
  copies_source_field_id?: number | null;
  copyingFields?: CopyingField[];
  checkedCopyingFields?: string[];
  disabledChildren?: string[];
};

type TreeFields = {
  title: string;
  lowercaseTitle?: string;
  key: string;
  isHidden?: boolean;
  isDisabled?: boolean;
  value: NodeValue;
  scopedSlots?: Record<string, string>;
  checkable?: boolean;
  selectable?: boolean;
  hasIcon?: boolean;
  tooltipText?: string;
  isRequired?: boolean;
  blockedPops?: { id: number; name: string }[];
  tableName?: string;
  fieldTooltipText?: string | null;
  table?: string | null;
};

type FieldLike = Pick<
  SourceField,
  | 'id'
  | 'display_name'
  | 'source_id'
  | 'relationship_id'
  | 'copies_source_field_id'
  | 'column'
>;
type SourceLike = Pick<Source, 'id' | 'table'> & {
  relationships?: Pick<
    NonNullable<Source['relationships']>[number],
    'id' | 'references_source_id'
  >[];
};

type FieldNode = FieldLike & TreeFields;
type LookupChildNode = FieldLike & TreeFields & { copiedField?: SourceField };
type LookupParentNode = FieldLike &
  TreeFields & { children: LookupChildNode[] };
type SourceChildNode = FieldNode | LookupParentNode;
type HeaderNode = {
  checkable: boolean;
  class: string;
  title: string;
  lowercaseTitle?: string;
  tooltipText?: string;
  selectable?: boolean;
};
type SourceNode = SourceLike & TreeFields & { children: SourceChildNode[] };
type RawSourceNode = SourceLike &
  TreeFields & { children: (SourceChildNode | HeaderNode)[] };
type AnyTreeNode = SourceNode | SourceChildNode | LookupChildNode;
type DisplayNode = TreeNodeType<AnyTreeNode>;

type NodeCheckInfo = {
  checkedKeys: string[];
  nodeInfo: {
    checked: boolean;
    node: { value: NodeValue };
  };
};

type TooltipOptions = {
  lookupFieldId: number | undefined;
  relId: string | null | undefined;
  lookupSourceId: number | undefined;
  copiedFieldId: number | null | undefined;
  copiedSourceId: number;
  messageTypeLookup: Message;
  wasLookupFieldChecked: boolean;
};

const {
  fieldsPending = false,
  initialAllFields = [],
  initialSelectedFieldsOnly = [],
  requiredColumns = {},
  selectedFields = [],
} = defineProps<{
  fieldsPending?: boolean;
  initialAllFields?: RawSourceNode[];
  initialSelectedFieldsOnly?: RawSourceNode[];
  requiredColumns?: Record<string, string[]>;
  selectedFields?: string[];
}>();

const emit = defineEmits<{
  refreshFields: [];
  'fields-changed': [info: NodeCheckInfo];
}>();

function withoutHeaders(sources: RawSourceNode[]): SourceNode[] {
  return sources.map((source) => ({
    ...source,
    children: source.children.filter(
      (child): child is SourceChildNode => child.checkable !== false,
    ),
  }));
}

const sourcesStore = useSourcesStore();

const activeTab = ref<string>(SELECTED_FIELDS);
const allFields = ref<SourceNode[]>([]);
const selectedFieldsOnly = ref<SourceNode[]>([]);
const searchQuery = ref('');

const tabs = computed(() => [
  { name: 'Selected Fields', value: ALL_FIELDS, disabled: fieldsPending },
  { name: 'All Fields', value: SELECTED_FIELDS, disabled: fieldsPending },
]);

const activeTabName = computed(
  () => tabs.value.find((tab) => tab.value === activeTab.value)?.name,
);

const activeSourceArray = computed(() =>
  activeTab.value === ALL_FIELDS ? selectedFieldsOnly.value : allFields.value,
);
const selectedFieldsSet = computed(() => new Set(selectedFields));
const displayTree = computed(() => activeSourceArray.value.map(toDisplayNode));
const fullActiveTree = computed(() =>
  activeSourceArray.value.map(toCascadeNode),
);

watch(
  () => initialAllFields,
  () => {
    allFields.value = withoutHeaders(initialAllFields);
  },
);

watch(
  () => initialSelectedFieldsOnly,
  () => {
    selectedFieldsOnly.value = withoutHeaders(initialSelectedFieldsOnly);
  },
);

watch(searchQuery, () => {
  activeTab.value = ALL_FIELDS;
});

onMounted(async () => {
  await sourcesStore.readySync;
  allFields.value = withoutHeaders(initialAllFields);
  selectedFieldsOnly.value = withoutHeaders(initialSelectedFieldsOnly);
});

function isFullyChecked(node: AnyTreeNode): boolean {
  const children = 'children' in node ? node.children : undefined;
  const checkable = (children ?? []).filter(
    (child) => child.checkable !== false && !child.isDisabled,
  );
  if (!checkable.length) return selectedFieldsSet.value.has(node.key);
  return checkable.every((child) => isFullyChecked(child));
}

function toTreeNode(
  node: AnyTreeNode,
  options: {
    filterHidden: boolean;
    getChecked: (node: AnyTreeNode) => boolean;
    getCheckable: (node: AnyTreeNode) => boolean;
  },
): DisplayNode {
  const rawChildren = 'children' in node ? node.children : undefined;
  const visibleChildren =
    rawChildren && options.filterHidden
      ? rawChildren.filter((child) => !child.isHidden)
      : rawChildren;
  return {
    id: node.key,
    label: node.title,
    checked: options.getChecked(node),
    isCheckable: options.getCheckable(node),
    isDisabled: !!node.isDisabled,
    children: visibleChildren?.map((child) => toTreeNode(child, options)),
    metaData: node,
  };
}

function toDisplayNode(node: AnyTreeNode): DisplayNode {
  return toTreeNode(node, {
    filterHidden: true,
    getChecked: isFullyChecked,
    getCheckable: (child) => child.checkable !== false,
  });
}

function toCascadeNode(node: AnyTreeNode): DisplayNode {
  return toTreeNode(node, {
    filterHidden: false,
    getChecked: (child) => selectedFieldsSet.value.has(child.key),
    getCheckable: (child) => child.checkable !== false && !child.isDisabled,
  });
}

function collectCheckedKeys(nodes: DisplayNode[]): string[] {
  return nodes.reduce<string[]>((keys, node) => {
    if (node.checked && node.metaData?.key) keys.push(node.metaData.key);
    if (node.children) keys.push(...collectCheckedKeys(node.children));
    return keys;
  }, []);
}

function onUpdate(updatedTree: DisplayNode[], updatedNodeId: number | string) {
  const toggled = findNode(updatedTree, updatedNodeId);
  const value = toggled?.metaData?.value;
  if (!value) return;
  const checked = !!toggled.checked;
  const fullTree = applyCheckedChange(
    fullActiveTree.value,
    updatedNodeId,
    checked,
  );
  const checkedKeys = collectCheckedKeys(fullTree);
  onFieldSelected({ checkedKeys, nodeInfo: { checked, node: { value } } });
}

function tooltipFor(meta: AnyTreeNode | undefined) {
  if (!meta?.fieldTooltipText) return { is: 'div', props: {} };
  return {
    is: BittsTooltip,
    props: {
      hideArrow: false,
      mountToBody: true,
      overlayClass: 'c-bitts-tree_question-tooltip-overlay',
      placement: 'top',
      trigger: 'hover',
    },
  };
}

function enableNode(node: { isDisabled?: boolean; isHidden?: boolean }) {
  node.isDisabled = false;
  node.isHidden = false;
}

function disableChildField(fields: SourceNode[], child: SourceChildNode) {
  const parentSource = fields.find((source) => source.id === child.source_id);
  if (!parentSource) return fields;
  parentSource.children = parentSource.children.map((fieldChild) => {
    if (fieldChild.id === child.id) {
      fieldChild.isDisabled = true;
    }
    return fieldChild;
  });
  return fields;
}

function addFieldToSelectedFieldsOnly(source: SourceNode, fieldId: number) {
  source.children = source.children.map((child) => {
    if (child.id === fieldId) {
      const newChild = { ...child };
      newChild.isHidden = false;
      if (newChild.value.required) {
        newChild.isDisabled = true;
        allFields.value = disableChildField(allFields.value, child);
      }
      return newChild;
    }
    return child;
  });
}

function checkLookupField(value: NodeValue) {
  const {
    source_id: sourceId,
    copies_source_field_id: copyId,
    relationship_id: relId,
    id,
  } = value;
  const source = selectedFieldsOnly.value.find(
    (source) => source.id === sourceId,
  );
  if (!source) return;
  disableRequiredFields(source);
  const isParentLookupField = !copyId;
  const parentLookupField = source.children.find(
    (field) => field.relationship_id === relId && 'children' in field,
  );
  const relationship = source.relationships?.find((rel) => rel.id === relId);
  if (!relationship || !parentLookupField || !('children' in parentLookupField))
    return;
  const copiedFieldSourceId = relationship.references_source_id;
  if (isParentLookupField) {
    parentLookupField.children.forEach((child) => {
      if (!child.value.requiresUncheckedField) {
        child.isHidden = false;
        if (child.copies_source_field_id != null) {
          disableCopiedSourceField(
            child.copies_source_field_id,
            copiedFieldSourceId,
          );
        }
        updateTooltipText({
          lookupFieldId: child.id,
          relId,
          lookupSourceId: sourceId,
          copiedFieldId: child.copies_source_field_id,
          copiedSourceId: copiedFieldSourceId,
          messageTypeLookup: SYNCED,
          wasLookupFieldChecked: true,
        });
      }
    });
  } else {
    const selectedLookupField = parentLookupField.children.find(
      (field) => field.id === id,
    );
    if (selectedLookupField) {
      selectedLookupField.isHidden = false;
    }
    if (copyId != null) disableCopiedSourceField(copyId, copiedFieldSourceId);
    updateTooltipText({
      lookupFieldId: id,
      relId,
      lookupSourceId: sourceId,
      copiedFieldId: copyId,
      copiedSourceId: copiedFieldSourceId,
      messageTypeLookup: SYNCED,
      wasLookupFieldChecked: true,
    });
  }
}

function checkSource(value: NodeValue) {
  const source = selectedFieldsOnly.value.find(
    (source) => source.id === value.id,
  );
  if (!source) return;
  source.children.forEach((child) => {
    child.isHidden = false;
    if ('children' in child) {
      child.children.forEach((grandchild) => {
        grandchild.isHidden = false;
      });
    }
    if (child.value.copyingFields) {
      child.value.copyingFields.forEach((copyingField) => {
        const { lookupFieldAll, lookupFieldSelected } =
          enableLookupField(copyingField);
        const fullField = sourcesStore.getSourceFieldById(child.value.id);
        const tooltipText = getFieldTooltipText(fullField, null, UNSYNCED);
        if (lookupFieldAll) lookupFieldAll.fieldTooltipText = tooltipText;
        if (lookupFieldSelected)
          lookupFieldSelected.fieldTooltipText = tooltipText;
      });
    }
    if (child.value.required) {
      allFields.value = disableChildField(allFields.value, child);
      selectedFieldsOnly.value = disableChildField(
        selectedFieldsOnly.value,
        child,
      );
    }
  });
}

function checkSourceField(value: NodeValue) {
  const { source_id: sourceId, id: fieldId } = value;
  const source = selectedFieldsOnly.value.find(
    (source) => source.id === sourceId,
  );
  if (!source) return;
  addFieldToSelectedFieldsOnly(source, fieldId);
  disableRequiredFields(source);
  if (value.copyingFields) {
    value.copyingFields.forEach((copyingField) => {
      const { lookupFieldAll, lookupFieldSelected } =
        enableLookupField(copyingField);
      const fullField = sourcesStore.getSourceFieldById(value.id);
      const tooltipText = getFieldTooltipText(fullField, null, UNSYNCED);
      if (lookupFieldAll) lookupFieldAll.fieldTooltipText = tooltipText;
      if (lookupFieldSelected)
        lookupFieldSelected.fieldTooltipText = tooltipText;
    });
  }
}

function disableCopiedSourceField(
  copiedFieldId: number,
  copiedSourceId: number,
) {
  const { sourceFieldAll, sourceFieldSelected, sourceAll, sourceSelected } =
    findSourceFields({ copiedFieldId, copiedSourceId });
  if (!sourceFieldAll || !sourceFieldSelected || !sourceAll || !sourceSelected)
    return;
  if (!sourceFieldAll.value.required) {
    sourceFieldAll.isDisabled = true;
    sourceFieldSelected.isDisabled = true;
  }
  sourceFieldAll.value.requiredForLookup = true;
  sourceFieldSelected.value.requiredForLookup = true;
  const copiedFieldKey = getKey({
    type: SOURCE_FIELD,
    sourceId: copiedSourceId,
    fieldId: copiedFieldId,
  });
  const checkedCopyingFields = [
    ...(sourceAll.value.checkedCopyingFields ?? []),
    copiedFieldKey,
  ];
  sourceAll.value = { ...sourceAll.value, checkedCopyingFields };
  sourceSelected.value = { ...sourceSelected.value, checkedCopyingFields };
}

function disableLookupField({
  copyingId,
  copyingRelId,
  copyingSourceId,
}: {
  copyingId: number;
  copyingRelId: string | null | undefined;
  copyingSourceId: number | undefined;
}) {
  if (isFieldChecked(copyingId))
    return { lookupFieldAll: undefined, lookupFieldSelected: undefined };
  const {
    lookupFieldAll,
    lookupFieldSelected,
    parentLookupFieldAll,
    parentLookupFieldSelected,
  } = findLookupFields({ copyingId, copyingRelId, copyingSourceId });
  if (lookupFieldAll) {
    lookupFieldAll.isDisabled = true;
    lookupFieldAll.value.requiresUncheckedField = true;
  }
  if (lookupFieldSelected) {
    lookupFieldSelected.isDisabled = true;
  }
  if (parentLookupFieldAll && 'children' in parentLookupFieldAll) {
    const newDisabledChildrenList = getDisabledChildKeys(parentLookupFieldAll);
    parentLookupFieldAll.value.disabledChildren = newDisabledChildrenList;
    if (parentLookupFieldSelected)
      parentLookupFieldSelected.value.disabledChildren =
        newDisabledChildrenList;
  }
  return { lookupFieldAll, lookupFieldSelected };
}

function disableRequiredFields(source: SourceNode) {
  if (!source.value.hasRequiredFields) return;
  source.children.forEach((child) => {
    if (child.value.required) {
      allFields.value = disableChildField(allFields.value, child);
      selectedFieldsOnly.value = disableChildField(
        selectedFieldsOnly.value,
        child,
      );
    }
  });
}

function enableCopiedSourceField(
  copiedFieldId: number,
  copiedSourceId: number,
  lookupFieldId: number | undefined,
) {
  const { sourceFieldAll, sourceFieldSelected, sourceAll, sourceSelected } =
    findSourceFields({ copiedFieldId, copiedSourceId });
  if (!sourceFieldAll || !sourceFieldSelected || !sourceAll || !sourceSelected)
    return;
  const checkedFieldsForSourceField = getCheckedFieldsForSource(
    sourceFieldAll.value.copyingFields ?? [],
    lookupFieldId,
    false,
  );
  if (checkedFieldsForSourceField.length) return;
  const copiedFieldKey = getKey({
    type: SOURCE_FIELD,
    sourceId: copiedSourceId,
    fieldId: copiedFieldId,
  });
  const checkedCopyingFields = [
    ...(sourceAll.value.checkedCopyingFields ?? []),
  ].filter((key) => key !== copiedFieldKey);
  sourceAll.value = { ...sourceAll.value, checkedCopyingFields };
  sourceSelected.value = { ...sourceSelected.value, checkedCopyingFields };
  if (!sourceFieldAll.value.required) {
    enableNode(sourceFieldAll);
    enableNode(sourceFieldSelected);
  }
  sourceFieldAll.value.requiredForLookup = false;
  sourceFieldSelected.value.requiredForLookup = false;
}

function enableLookupField({
  copyingId,
  copyingRelId,
  copyingSourceId,
}: {
  copyingId: number;
  copyingRelId: string | null | undefined;
  copyingSourceId: number | undefined;
}) {
  const {
    lookupFieldAll,
    lookupFieldSelected,
    parentLookupFieldAll,
    parentLookupFieldSelected,
  } = findLookupFields({ copyingId, copyingRelId, copyingSourceId });
  if (lookupFieldAll) {
    enableNode(lookupFieldAll);
    lookupFieldAll.value.requiresUncheckedField = false;
  }
  if (lookupFieldSelected) {
    enableNode(lookupFieldSelected);
    lookupFieldSelected.value.requiresUncheckedField = false;
  }
  if (parentLookupFieldAll && 'children' in parentLookupFieldAll) {
    const newDisabledChildrenList = getDisabledChildKeys(parentLookupFieldAll);
    parentLookupFieldAll.value.disabledChildren = newDisabledChildrenList;
    if (parentLookupFieldSelected)
      parentLookupFieldSelected.value.disabledChildren =
        newDisabledChildrenList;
  }
  return { lookupFieldAll, lookupFieldSelected };
}

function enableRequiredField(sourceIndex: number, childIndex: number) {
  const selectedChild =
    selectedFieldsOnly.value[sourceIndex]?.children[childIndex];
  const allChild = allFields.value[sourceIndex]?.children[childIndex];
  if (selectedChild) {
    enableNode(selectedChild);
  }
  if (allChild) {
    enableNode(allChild);
  }
}

function findLookupFields({
  copyingId,
  copyingRelId,
  copyingSourceId,
}: {
  copyingId: number | undefined;
  copyingRelId: string | null | undefined;
  copyingSourceId: number | undefined;
}) {
  const copiedSourceIndex = allFields.value.findIndex(
    (source) => source.id === copyingSourceId,
  );
  const sourceAll = allFields.value[copiedSourceIndex];
  const sourceSelected = selectedFieldsOnly.value[copiedSourceIndex];
  const parentLookupFieldIndex =
    sourceAll?.children.findIndex(
      (field) => field.relationship_id === copyingRelId && 'children' in field,
    ) ?? -1;
  const parentLookupFieldAll = sourceAll?.children[parentLookupFieldIndex];
  const parentLookupFieldSelected =
    sourceSelected?.children[parentLookupFieldIndex];
  const lookupFieldIndex =
    parentLookupFieldAll && 'children' in parentLookupFieldAll
      ? parentLookupFieldAll.children.findIndex((lf) => lf.id === copyingId)
      : -1;
  const lookupFieldAll =
    parentLookupFieldAll && 'children' in parentLookupFieldAll
      ? parentLookupFieldAll.children[lookupFieldIndex]
      : undefined;
  const lookupFieldSelected =
    parentLookupFieldSelected && 'children' in parentLookupFieldSelected
      ? parentLookupFieldSelected.children[lookupFieldIndex]
      : undefined;
  return {
    lookupFieldAll,
    lookupFieldSelected,
    parentLookupFieldAll,
    parentLookupFieldSelected,
  };
}

function findSourceFields({
  copiedFieldId,
  copiedSourceId,
}: {
  copiedFieldId: number | null | undefined;
  copiedSourceId: number;
}) {
  const sourceIndex = allFields.value.findIndex(
    (source) => source.id === copiedSourceId,
  );
  const sourceAll = allFields.value[sourceIndex];
  const sourceSelected = selectedFieldsOnly.value[sourceIndex];
  const fieldIndex =
    sourceAll?.children.findIndex((field) => field.id === copiedFieldId) ?? -1;
  return {
    sourceFieldAll: sourceAll?.children[fieldIndex],
    sourceFieldSelected: sourceSelected?.children[fieldIndex],
    sourceAll,
    sourceSelected,
  };
}

function getCheckedFieldsForSource(
  copyingFields: CopyingField[],
  lookupFieldId: number | undefined,
  wasLookupFieldChecked: boolean,
) {
  return copyingFields.filter((field) => {
    const isCurrentUncheckedField =
      !wasLookupFieldChecked && field.copyingId === lookupFieldId;
    if (isCurrentUncheckedField) return false;
    const isCurrentCheckedField =
      wasLookupFieldChecked && field.copyingId === lookupFieldId;
    return (
      isCurrentCheckedField ||
      selectedFields.some((key) => key.includes(field.copyingId.toString()))
    );
  });
}

function getDisabledChildKeys(lookupParent: LookupParentNode) {
  return lookupParent.children
    .filter((child) => child.value.requiresUncheckedField)
    .map((child) => child.key);
}

function isFieldChecked(id: number) {
  return selectedFields.some((key) => key.includes(id.toString()));
}

function onFieldSelected(info: NodeCheckInfo) {
  const { checked } = info.nodeInfo;
  const checkedNodeValue = info.nodeInfo.node.value;
  if (!checked && checkedNodeValue.required) return;
  if (
    checkedNodeValue.requiresUncheckedField ||
    checkedNodeValue.requiredForLookup ||
    checkedNodeValue.hasCheckedCopyingFields
  )
    return;
  emit('fields-changed', info);
  switch (checkedNodeValue.type) {
    case SOURCE:
      checked ? checkSource(checkedNodeValue) : uncheckSource(checkedNodeValue);
      break;
    case SOURCE_FIELD:
      checked
        ? checkSourceField(checkedNodeValue)
        : uncheckSourceField(checkedNodeValue);
      break;
    case LOOKUP_FIELD:
      checked
        ? checkLookupField(checkedNodeValue)
        : uncheckLookupField(checkedNodeValue);
      break;
    default:
      break;
  }
}

function uncheckLookupField(value: NodeValue) {
  const {
    source_id: sourceId,
    copies_source_field_id: copyId,
    relationship_id: relId,
    id,
  } = value;
  const source = selectedFieldsOnly.value.find(
    (source) => source.id === sourceId,
  );
  if (!source) return;
  const parentLookupField = source.children.find(
    (field) => field.relationship_id === relId && 'children' in field,
  );
  const isParentLookupField = !copyId;
  const relationship = source.relationships?.find((rel) => rel.id === relId);
  if (!relationship) return;
  const copiedFieldSourceId = relationship.references_source_id;
  if (isParentLookupField) {
    if (parentLookupField && 'children' in parentLookupField) {
      parentLookupField.children.forEach((child) => {
        if (child.copies_source_field_id != null) {
          enableCopiedSourceField(
            child.copies_source_field_id,
            copiedFieldSourceId,
            child.id,
          );
        }
        updateTooltipText({
          lookupFieldId: child.id,
          relId,
          lookupSourceId: sourceId,
          copiedFieldId: child.copies_source_field_id,
          copiedSourceId: copiedFieldSourceId,
          messageTypeLookup: UNSYNCED,
          wasLookupFieldChecked: false,
        });
      });
    }
  } else {
    if (copyId != null)
      enableCopiedSourceField(copyId, copiedFieldSourceId, id);
    updateTooltipText({
      lookupFieldId: id,
      relId,
      lookupSourceId: sourceId,
      copiedFieldId: copyId,
      copiedSourceId: copiedFieldSourceId,
      messageTypeLookup: UNSYNCED,
      wasLookupFieldChecked: false,
    });
  }
}

function uncheckSource(value: NodeValue) {
  if (!value.hasRequiredFields) return;
  const sourceIndex = selectedFieldsOnly.value.findIndex(
    (source) => source.id === value.id,
  );
  const source = selectedFieldsOnly.value[sourceIndex];
  if (!source) return;
  const requiredColumnsForSource =
    (source.table ? requiredColumns[source.table] : undefined) ?? [];
  source.children.forEach((child, index) => {
    if (child.column && requiredColumnsForSource.includes(child.column)) {
      enableRequiredField(sourceIndex, index);
    }
  });
}

function uncheckSourceField(value: NodeValue) {
  if (!value.copyingFields) return;
  value.copyingFields.forEach((copyingField) => {
    const { lookupFieldAll, lookupFieldSelected } =
      disableLookupField(copyingField);
    const fullField = sourcesStore.getSourceFieldById(value.id);
    const tooltipText = getFieldTooltipText(fullField, null, UNSYNCED_DISABLED);
    if (lookupFieldAll) lookupFieldAll.fieldTooltipText = tooltipText;
    if (lookupFieldSelected) lookupFieldSelected.fieldTooltipText = tooltipText;
  });
}

function updateTooltipText({
  lookupFieldId,
  relId,
  lookupSourceId,
  copiedFieldId,
  copiedSourceId,
  messageTypeLookup,
  wasLookupFieldChecked,
}: TooltipOptions) {
  const {
    lookupFieldAll,
    lookupFieldSelected,
    parentLookupFieldAll,
    parentLookupFieldSelected,
  } = findLookupFields({
    copyingId: lookupFieldId,
    copyingRelId: relId,
    copyingSourceId: lookupSourceId,
  });
  const { sourceFieldAll, sourceFieldSelected, sourceAll, sourceSelected } =
    findSourceFields({ copiedFieldId, copiedSourceId });

  if (lookupFieldAll)
    lookupFieldAll.fieldTooltipText = getFieldTooltipText(
      sourceFieldAll,
      sourceAll ?? null,
      messageTypeLookup,
    );
  if (lookupFieldSelected)
    lookupFieldSelected.fieldTooltipText = getFieldTooltipText(
      sourceFieldSelected,
      sourceSelected ?? null,
      messageTypeLookup,
    );

  const checkedCopiedFields = getCheckedFieldsForSource(
    sourceFieldAll?.value.copyingFields ?? [],
    lookupFieldId,
    wasLookupFieldChecked,
  );
  let fieldMessageType: Message;
  if (!checkedCopiedFields.length) {
    fieldMessageType = UNSYNCED;
  } else {
    fieldMessageType =
      checkedCopiedFields.length > 1 ? CANNOT_UNSYNC_PLURAL : CANNOT_UNSYNC;
  }

  if (sourceFieldAll)
    sourceFieldAll.fieldTooltipText = getFieldTooltipText(
      checkedCopiedFields,
      parentLookupFieldAll ?? null,
      fieldMessageType,
    );
  if (sourceFieldSelected)
    sourceFieldSelected.fieldTooltipText = getFieldTooltipText(
      checkedCopiedFields,
      parentLookupFieldSelected ?? null,
      fieldMessageType,
    );
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.datasource-source-selector {
  @apply flex flex-col border border-neutral-border rounded-bts-base;

  :deep(.ant-input) {
    @apply rounded-bts-md bg-white;
  }
  :deep(.ant-tabs-content) {
    @apply max-h-[250px] overflow-y-auto overflow-x-hidden leading-normal px-0 pt-0;
  }
  :deep(.ant-tabs-nav .ant-tabs-tab) {
    @apply w-auto;
  }
  :deep(.ant-tabs-ink-bar) {
    @apply w-auto;
  }
  :deep(.ant-tabs-nav) {
    @apply py-0;
  }
  :deep(.bitts-tabs .ant-tabs-nav .ant-tabs-tab) {
    @apply px-0 mx-16 text-m;
  }
}

.datasource-source-selector__tabs {
  @apply border-none;
}

.datasource-source-selector__tree {
  @apply px-16 pb-8;
}

.datasource-source-selector__node {
  @apply w-full inline-flex justify-between items-center;
}

.datasource-source-selector__label {
  @apply inline;
  color: inherit;
}

.datasource-source-selector__node-disabled {
  @apply cursor-not-allowed text-neutral-text/50;
}

.c-bitts-tree_population-links {
  @apply flex bg-neutral-background-weak flex-col p-8 mr-16 cursor-default;
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";
/* These mount on the body via BittsTooltip, so they cannot be scoped. */
.c-bitts-tree_question-tooltip-overlay {
  @apply flex items-center;
  .ant-tooltip-inner {
    @apply opacity-100 bg-neutral-background-strong;
  }
}
.c-bitts-tree_question-tooltip-overlay svg:focus {
  @apply outline-hidden;
}
.c-bitts-tree_question-tooltip {
  @apply flex items-center flex-wrap;
  max-width: 200px;
}
</style>
